import {
  useEffect,
  useRef,
  useState,
  useSyncExternalStore,
  type FormEvent,
} from "react";
import { AccountSuccess } from "./AccountSuccess";
import taibahUniversityPrimary from "../../../design/source-assets/taibah-university-primary.png";
import taibahUniversityWhite from "../../../design/source-assets/taibah-university-white.png";
import taibahUniversityVerticalPrimary from "../../../design/source-assets/taibah-university-vertical-primary.png";
import taibahUniversityVerticalWhite from "../../../design/source-assets/taibah-university-vertical-white.png";
import type { AuthFlowController } from "./auth-flow";
import type { SafeAuthErrorCode } from "./auth-types";
import {
  CalendarIcon,
  ChartIcon,
  EyeIcon,
  GoogleIcon,
  LinkIcon,
  MoonIcon,
  ReceiptIcon,
  SignOutIcon,
  SunIcon,
  UsersIcon,
  WalletIcon,
} from "./icons";
import { PortfolioOverviewPanel } from "./PortfolioOverviewPanel";
import { PartnerSignature } from "./PartnerSignature";
import { ConferenceDirectoryPanel } from "./ConferenceDirectoryPanel";
import { ConferenceDetailPanel } from "./ConferenceDetailPanel";
import { SponsorshipsPanel } from "./SponsorshipsPanel";
import { ExpensesPanel } from "./ExpensesPanel";
import { ConnectConferencePanel } from "./ConnectConferencePanel";
import { AdminUsersPanel } from "./AdminUsersPanel";
import { UserAccessPanel } from "./UserAccessPanel";
import { RevenueDraftNavigationContext } from "./useRevenueDraft";
import type { AdminUsersClient } from "./admin-users-api";
import { parseAppRoute } from "./conference-directory-model";
import type { ConferenceDashboardClient } from "./portfolio-overview-api";
import { isEmbeddedGoogleOAuthBrowser } from "./oauth-browser-support";
import {
  generateTotpQrDataUri,
  TOTP_ISSUER,
  validateTotpEnrollmentUri,
  type TotpEnrollmentMetadata,
} from "./totp-qr";
import { normalizeMfaDigits } from "./mfa-code";
import {
  isolateLtr,
  useLocale,
  type AppLocale,
  type MessageKey,
} from "./localization";
import {
  applyThemeToDocument,
  persistTheme,
  resolveInitialTheme,
  type AppTheme,
} from "./theme";
import "./app-navigation.css";

interface AuthAppProps {
  readonly controller: AuthFlowController;
  readonly overviewClient: ConferenceDashboardClient;
  readonly adminUsersClient: AdminUsersClient;
}

interface ThemeControlProps {
  readonly theme: AppTheme;
  readonly onToggleTheme: () => void;
}

const AUTH_ERROR_KEYS: Readonly<Record<SafeAuthErrorCode, MessageKey>> =
  Object.freeze({
    "invalid-credentials": "auth.error.invalidCredentials",
    "account-disabled": "auth.error.accountDisabled",
    "account-not-provisioned": "auth.error.accountNotProvisioned",
    "access-denied": "auth.error.accessDenied",
    "google-sign-in-unavailable": "auth.error.googleUnavailable",
    "invalid-mfa-code": "auth.error.invalidMfa",
    "network-unavailable": "auth.error.network",
    "popup-closed": "auth.error.popupClosed",
    "session-expired": "auth.error.sessionExpired",
    "unexpected-error": "auth.error.unexpected",
  });

function LocaleToggle({ className = "" }: { readonly className?: string }) {
  const { locale, setLocale, t } = useLocale();
  const nextLocale: AppLocale = locale === "ar-SA" ? "en" : "ar-SA";
  const language = nextLocale === "en" ? "English" : "العربية";

  return (
    <button
      className={`locale-toggle ${className}`.trim()}
      type="button"
      aria-label={t("locale.switchTo", { language })}
      onClick={() => setLocale(nextLocale)}
    >
      <span
        lang={nextLocale === "en" ? "en" : "ar"}
        dir={nextLocale === "en" ? "ltr" : "rtl"}
      >
        {language}
      </span>
    </button>
  );
}

function ThemeToggle({ theme, onToggleTheme }: ThemeControlProps) {
  const { t } = useLocale();
  const darkThemeActive = theme === "dark";

  return (
    <button
      className="icon-button"
      type="button"
      aria-label={darkThemeActive ? t("theme.useLight") : t("theme.useDark")}
      aria-pressed={darkThemeActive}
      title={darkThemeActive ? t("theme.useLight") : t("theme.useDark")}
      onClick={onToggleTheme}
    >
      {darkThemeActive ? <SunIcon /> : <MoonIcon />}
    </button>
  );
}

function BrandMark({
  compact = false,
  inverse = false,
  decorative = false,
  layout = "horizontal",
}: {
  readonly compact?: boolean;
  readonly inverse?: boolean;
  readonly decorative?: boolean;
  readonly layout?: "horizontal" | "vertical";
}) {
  const { t } = useLocale();
  const className = [
    "brand-mark",
    compact ? "brand-mark--compact" : "",
    inverse ? "brand-mark--inverse" : "",
    layout === "vertical" ? "brand-mark--vertical" : "",
  ]
    .filter(Boolean)
    .join(" ");
  return (
    <span className={className} translate="no">
      <img
        className="brand-mark__dark"
        src={
          layout === "vertical"
            ? taibahUniversityVerticalWhite
            : taibahUniversityWhite
        }
        alt={decorative ? "" : t("brand.taibahUniversity")}
        aria-hidden={decorative || undefined}
        width={layout === "vertical" ? 1856 : 1598}
        height={layout === "vertical" ? 1507 : 367}
      />
      <img
        className="brand-mark__light"
        src={
          layout === "vertical"
            ? taibahUniversityVerticalPrimary
            : taibahUniversityPrimary
        }
        alt={decorative ? "" : t("brand.taibahUniversity")}
        aria-hidden={decorative || undefined}
        width={layout === "vertical" ? 1856 : 1598}
        height={layout === "vertical" ? 1507 : 367}
      />
    </span>
  );
}

function SignInForm({
  controller,
  theme,
  onToggleTheme,
}: Pick<AuthAppProps, "controller"> & ThemeControlProps) {
  const { t } = useLocale();
  const state = useSyncExternalStore(
    controller.subscribe,
    controller.getSnapshot,
    controller.getSnapshot,
  );
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const embeddedGoogleOAuth = isEmbeddedGoogleOAuthBrowser(
    window.navigator.userAgent,
  );

  if (state.view !== "sign-in") return null;

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    void controller.signInWithPassword(email, password);
  }

  return (
    <section className="auth-form-panel" aria-labelledby="sign-in-title">
      <div className="auth-form-panel__inner">
        <div className="auth-form-panel__utilities utility-bar__actions">
          <LocaleToggle />
          <ThemeToggle theme={theme} onToggleTheme={onToggleTheme} />
        </div>
        <BrandMark compact />
        <header className="auth-heading">
          <h1 id="sign-in-title">{t("auth.dashboardLabel")}</h1>
          <p>{t("auth.subtitle")}</p>
        </header>

        <form className="auth-form" onSubmit={submit} noValidate={false}>
          <label htmlFor="email">{t("auth.email")}</label>
          <input
            id="email"
            name="email"
            type="email"
            autoComplete="username"
            inputMode="email"
            spellCheck={false}
            aria-describedby={state.errorCode ? "sign-in-error" : undefined}
            aria-invalid={
              state.errorCode === "invalid-credentials" || undefined
            }
            required
            disabled={state.busy}
            dir="ltr"
            value={email}
            onChange={(event) => setEmail(event.currentTarget.value)}
          />

          <label htmlFor="password">{t("auth.password")}</label>
          <div className="password-control">
            <input
              id="password"
              name="password"
              type={showPassword ? "text" : "password"}
              autoComplete="current-password"
              aria-describedby={state.errorCode ? "sign-in-error" : undefined}
              aria-invalid={
                state.errorCode === "invalid-credentials" || undefined
              }
              dir="ltr"
              required
              disabled={state.busy}
              value={password}
              onChange={(event) => setPassword(event.currentTarget.value)}
            />
            <button
              type="button"
              className="icon-button password-toggle"
              aria-label={
                showPassword ? t("auth.hidePassword") : t("auth.showPassword")
              }
              aria-pressed={showPassword}
              onClick={() => setShowPassword((current) => !current)}
            >
              <EyeIcon />
            </button>
          </div>

          {state.errorCode ? (
            <p
              id="sign-in-error"
              className="form-message form-message--error"
              role="alert"
            >
              {t(AUTH_ERROR_KEYS[state.errorCode])}
            </p>
          ) : null}
          {state.notice ? (
            <p className="form-message form-message--success" role="status">
              {t("auth.mfaConfiguredNotice")}
            </p>
          ) : null}

          <button
            className="button button--primary"
            type="submit"
            disabled={state.busy}
          >
            {state.busy ? t("auth.signingIn") : t("auth.signIn")}
          </button>
        </form>

        <div className="auth-divider" aria-hidden="true">
          <span>{t("auth.or")}</span>
        </div>

        {embeddedGoogleOAuth ? (
          <div className="embedded-browser-notice" role="status">
            <strong>{t("auth.embeddedTitle")}</strong>
            <span>{t("auth.embeddedBody")}</span>
            <a
              className="button button--secondary"
              href={window.location.href}
              target="_blank"
              rel="noreferrer"
            >
              {t("auth.openDashboardLink")}
            </a>
          </div>
        ) : (
          <>
            <button
              className="button button--google"
              type="button"
              disabled={state.busy}
              onClick={() => void controller.signInWithGoogle()}
            >
              <GoogleIcon />
              {t("auth.continueGoogle")}
            </button>
            <p className="oauth-browser-help">{t("auth.oauthHelp")}</p>
          </>
        )}
        <p className="authorized-notice">{t("auth.authorizedOnly")}</p>

        <PartnerSignature placement="mobile" />
      </div>
    </section>
  );
}

function MfaPanel({
  controller,
  theme,
  onToggleTheme,
}: Pick<AuthAppProps, "controller"> & ThemeControlProps) {
  const { number, t } = useLocale();
  const state = useSyncExternalStore(
    controller.subscribe,
    controller.getSnapshot,
    controller.getSnapshot,
  );
  const [code, setCode] = useState("");
  const [qrRetry, setQrRetry] = useState(0);
  const [copyState, setCopyState] = useState<
    "idle" | "copying" | "copied" | "failed"
  >("idle");
  const [qrState, setQrState] = useState<
    | { readonly status: "idle" }
    | {
        readonly status: "loading";
        readonly metadata: TotpEnrollmentMetadata;
      }
    | {
        readonly status: "ready";
        readonly metadata: TotpEnrollmentMetadata;
        readonly dataUri: string;
      }
    | { readonly status: "error" }
  >({ status: "idle" });
  const enrollment = state.view === "mfa-enrollment" ? state : null;
  const enrollmentSetupUrl = enrollment?.setupUrl ?? null;
  const enrollmentAccountLabel = enrollment?.accountLabel ?? null;
  const enrollmentSecretKey = enrollment?.secretKey ?? null;

  useEffect(() => {
    let cancelled = false;

    if (!enrollmentSetupUrl || !enrollmentAccountLabel) {
      setQrState({ status: "idle" });
      return () => {
        cancelled = true;
      };
    }

    try {
      const metadata = validateTotpEnrollmentUri(
        enrollmentSetupUrl,
        enrollmentAccountLabel,
      );
      setQrState({ status: "loading", metadata });
      void generateTotpQrDataUri(enrollmentSetupUrl, enrollmentAccountLabel)
        .then((dataUri) => {
          if (!cancelled) {
            setQrState({ status: "ready", metadata, dataUri });
          }
        })
        .catch(() => {
          if (!cancelled) setQrState({ status: "error" });
        });
    } catch {
      setQrState({ status: "error" });
    }

    return () => {
      cancelled = true;
    };
  }, [enrollmentAccountLabel, enrollmentSetupUrl, qrRetry]);

  useEffect(() => {
    setCopyState("idle");
  }, [enrollmentSecretKey]);

  if (state.view !== "mfa-challenge" && state.view !== "mfa-enrollment") {
    return null;
  }
  const enrolling = state.view === "mfa-enrollment";

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (enrolling) void controller.enrollMfa(code);
    else void controller.verifyMfa(code);
  }

  async function copySetupKey() {
    if (!enrollment || copyState === "copying") return;
    setCopyState("copying");
    try {
      if (!window.navigator.clipboard?.writeText) throw new Error();
      await window.navigator.clipboard.writeText(enrollment.secretKey);
      setCopyState("copied");
    } catch {
      setCopyState("failed");
    }
  }

  return (
    <main className="mfa-page">
      <section
        className={enrolling ? "mfa-panel mfa-panel--enrollment" : "mfa-panel"}
        aria-labelledby="mfa-title"
      >
        <div className="auth-form-panel__utilities utility-bar__actions mfa-panel__utilities">
          <LocaleToggle />
          <ThemeToggle theme={theme} onToggleTheme={onToggleTheme} />
        </div>
        <BrandMark compact />
        <header>
          <h1 id="mfa-title">
            {enrolling ? t("mfa.enrollTitle") : t("mfa.challengeTitle")}
          </h1>
          <p>
            {enrolling
              ? t("mfa.enrollInstruction")
              : t("mfa.challengeInstruction")}
          </p>
        </header>

        {enrolling ? (
          <div className="mfa-enrollment-grid">
            <section
              className="mfa-enrollment-step"
              aria-labelledby="mfa-scan-title"
            >
              <div className="mfa-step-heading">
                <span aria-hidden="true">{number(1)}</span>
                <div>
                  <h2 id="mfa-scan-title">{t("mfa.scanTitle")}</h2>
                  <p>{t("mfa.scanHelp")}</p>
                </div>
              </div>

              <div className="mfa-qr-frame">
                {qrState.status === "ready" ? (
                  <img
                    src={qrState.dataUri}
                    alt={t("mfa.qrAlt", {
                      issuer: isolateLtr(qrState.metadata.issuer),
                      account: isolateLtr(qrState.metadata.accountLabel),
                    })}
                    width="224"
                    height="224"
                    draggable={false}
                  />
                ) : qrState.status === "error" ? (
                  <div className="mfa-qr-message" role="alert">
                    <strong>{t("mfa.qrUnavailable")}</strong>
                    <span>{t("mfa.qrUnavailableBody")}</span>
                    <button
                      className="button button--secondary button--compact"
                      type="button"
                      onClick={() => setQrRetry((current) => current + 1)}
                    >
                      {t("mfa.qrRetry")}
                    </button>
                  </div>
                ) : (
                  <div
                    className="mfa-qr-message"
                    role="status"
                    aria-live="polite"
                    aria-busy={qrState.status === "loading"}
                  >
                    <span className="mfa-qr-loader" aria-hidden="true" />
                    <strong>{t("mfa.qrLoading")}</strong>
                  </div>
                )}
              </div>
            </section>

            <section
              className="mfa-enrollment-step mfa-enrollment-step--details"
              aria-labelledby="mfa-account-title"
            >
              <div className="mfa-step-heading">
                <span aria-hidden="true">{number(2)}</span>
                <div>
                  <h2 id="mfa-account-title">{t("mfa.accountTitle")}</h2>
                  <p>{t("mfa.accountHelp")}</p>
                </div>
              </div>

              <dl className="mfa-account-context">
                <div>
                  <dt>{t("mfa.service")}</dt>
                  <dd>
                    <bdi dir="ltr">{TOTP_ISSUER}</bdi>
                  </dd>
                </div>
                <div>
                  <dt>{t("mfa.account")}</dt>
                  <dd>
                    <bdi dir="ltr">{state.accountLabel}</bdi>
                  </dd>
                </div>
              </dl>

              <div className="mfa-manual-setup">
                <h3>{t("mfa.manualTitle")}</h3>
                <p>{t("mfa.manualHelp")}</p>
                <div className="mfa-manual-key-row">
                  <input
                    className="mfa-manual-key"
                    type="text"
                    aria-label={t("mfa.manualKeyAria")}
                    value={state.secretKey}
                    readOnly
                    autoComplete="off"
                    spellCheck={false}
                    dir="ltr"
                    onFocus={(event) => event.currentTarget.select()}
                  />
                  <button
                    className="button button--secondary button--compact"
                    type="button"
                    disabled={copyState === "copying"}
                    onClick={() => void copySetupKey()}
                  >
                    {copyState === "copying"
                      ? t("mfa.copying")
                      : t("mfa.copyKey")}
                  </button>
                </div>
                <p
                  className={
                    copyState === "failed"
                      ? "mfa-copy-status mfa-copy-status--error"
                      : "mfa-copy-status"
                  }
                  role={copyState === "failed" ? "alert" : "status"}
                  aria-live={copyState === "failed" ? "assertive" : "polite"}
                >
                  {copyState === "copied"
                    ? t("mfa.keyCopied")
                    : copyState === "failed"
                      ? t("mfa.copyFailed")
                      : ""}
                </p>
              </div>
            </section>
          </div>
        ) : null}

        {enrolling ? (
          <div className="mfa-step-heading mfa-step-heading--verification">
            <span aria-hidden="true">{number(3)}</span>
            <div>
              <h2>{t("mfa.verifySetupTitle")}</h2>
              <p>{t("mfa.verifySetupHelp")}</p>
            </div>
          </div>
        ) : null}

        <form className="auth-form" onSubmit={submit}>
          <label htmlFor="mfa-code">{t("mfa.codeLabel")}</label>
          <input
            id="mfa-code"
            name="mfa-code"
            className="mfa-code"
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            dir="ltr"
            pattern="[0-9٠-٩۰-۹]{6}"
            maxLength={6}
            required
            disabled={state.busy}
            aria-invalid={Boolean(state.errorCode)}
            aria-describedby={state.errorCode ? "mfa-code-error" : undefined}
            value={code}
            onChange={(event) =>
              setCode(normalizeMfaDigits(event.currentTarget.value))
            }
          />
          {state.errorCode ? (
            <p
              id="mfa-code-error"
              className="form-message form-message--error"
              role="alert"
            >
              {t(AUTH_ERROR_KEYS[state.errorCode])}
            </p>
          ) : null}
          <button
            className="button button--primary"
            type="submit"
            disabled={state.busy}
          >
            {state.busy
              ? t("mfa.verifying")
              : enrolling
                ? t("mfa.finishSetup")
                : t("mfa.verifyCode")}
          </button>
          <button
            className="button button--quiet"
            type="button"
            disabled={state.busy}
            onClick={() => void controller.cancelMfa()}
          >
            {t("mfa.backToSignIn")}
          </button>
        </form>
      </section>
    </main>
  );
}

const NAVIGATION = [
  {
    labelKey: "nav.overview",
    icon: ChartIcon,
    adminOnly: false,
    href: "#/",
    page: "overview",
  },
  {
    labelKey: "nav.conferences",
    icon: CalendarIcon,
    adminOnly: false,
    href: "#/conferences",
    page: "conferences",
  },
  {
    labelKey: "nav.revenue",
    icon: WalletIcon,
    adminOnly: true,
    href: "#/revenue",
    page: "revenue",
  },
  {
    labelKey: "nav.expenses",
    icon: ReceiptIcon,
    adminOnly: true,
    href: "#/expenses",
    page: "expenses",
  },
  {
    labelKey: "nav.users",
    icon: UsersIcon,
    adminOnly: true,
    href: "#/users",
    page: "users",
  },
  {
    labelKey: "nav.connect",
    icon: LinkIcon,
    adminOnly: true,
    href: "#/connect",
    page: "connect",
  },
] as const satisfies readonly {
  readonly labelKey: MessageKey;
  readonly icon: typeof ChartIcon;
  readonly adminOnly: boolean;
  readonly href: string;
  readonly page: string;
}[];

function AuthenticatedShell({
  controller,
  overviewClient,
  adminUsersClient,
  theme,
  onToggleTheme,
}: AuthAppProps & ThemeControlProps) {
  const { t } = useLocale();
  const state = useSyncExternalStore(
    controller.subscribe,
    controller.getSnapshot,
    controller.getSnapshot,
  );
  const [route, setRoute] = useState(() => parseAppRoute(window.location.hash));
  const acceptedRouteHash = useRef(window.location.hash);
  const navigationDrafts = useRef(new Set<string>());
  const [mobileMoreOpen, setMobileMoreOpen] = useState(false);
  const mobileMoreButtonRef = useRef<HTMLButtonElement>(null);
  const mobileMoreMenuRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const updateRoute = () => {
      const nextHash = window.location.hash;
      if (nextHash === acceptedRouteHash.current) return;
      if (
        navigationDrafts.current.size > 0 &&
        !window.confirm(t("finance.form.leaveConfirm"))
      ) {
        // hashchange is not cancelable. Restore the URL without another event;
        // keeping route state unchanged preserves the mounted, filled form.
        window.history.replaceState(
          window.history.state,
          "",
          `${window.location.pathname}${window.location.search}${acceptedRouteHash.current}`,
        );
        return;
      }
      acceptedRouteHash.current = nextHash;
      setRoute(parseAppRoute(nextHash));
      setMobileMoreOpen(false);
    };
    window.addEventListener("hashchange", updateRoute);
    return () => window.removeEventListener("hashchange", updateRoute);
  }, [t]);

  useEffect(() => {
    if (!mobileMoreOpen) return;
    mobileMoreMenuRef.current
      ?.querySelector<HTMLElement>("a[href], button:not([disabled])")
      ?.focus();

    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      setMobileMoreOpen(false);
      mobileMoreButtonRef.current?.focus();
    };
    const closeOnOutsidePointer = (event: PointerEvent) => {
      if (!(event.target instanceof Node)) return;
      if (
        !mobileMoreMenuRef.current?.contains(event.target) &&
        !mobileMoreButtonRef.current?.contains(event.target)
      ) {
        setMobileMoreOpen(false);
      }
    };

    document.addEventListener("keydown", closeOnEscape);
    document.addEventListener("pointerdown", closeOnOutsidePointer);
    return () => {
      document.removeEventListener("keydown", closeOnEscape);
      document.removeEventListener("pointerdown", closeOnOutsidePointer);
    };
  }, [mobileMoreOpen]);

  if (state.view !== "authenticated") return null;
  const visibleNavigation = NAVIGATION.filter(
    (item) => !item.adminOnly || state.session.role === "admin",
  );
  const mobilePrimaryNavigation = visibleNavigation.filter(
    (item) => item.page === "overview" || item.page === "conferences",
  );
  const mobileMoreNavigation = visibleNavigation.filter(
    (item) => item.page !== "overview" && item.page !== "conferences",
  );
  const mobileMoreSelected = mobileMoreNavigation.some(
    (item) => item.page === route.page,
  );

  return (
    <div className="app-shell app-shell--sidebar">
      <a
        className="skip-link"
        href="#main-content"
        onClick={(event) => {
          event.preventDefault();
          document.getElementById("main-content")?.focus();
        }}
      >
        {t("a11y.skipMain")}
      </a>
      <aside className="app-sidebar">
        <a
          className="app-sidebar__brand"
          href="#/"
          aria-label={t("auth.dashboardLabel")}
        >
          <BrandMark decorative layout="vertical" />
        </a>
        <nav
          className="app-navigation"
          aria-label={t("a11y.primaryNavigation")}
        >
          {visibleNavigation.map(({ labelKey, icon: Icon, href, page }) => (
            <a
              key={labelKey}
              className={
                route.page === page ||
                (page === "conferences" && route.page === "conference")
                  ? "nav-item nav-item--selected"
                  : "nav-item"
              }
              href={href}
              aria-label={t(labelKey)}
              aria-current={
                route.page === page ||
                (page === "conferences" && route.page === "conference")
                  ? "page"
                  : undefined
              }
            >
              <Icon />
              <span>{t(labelKey)}</span>
            </a>
          ))}
        </nav>
      </aside>

      <div className="app-shell__main">
        <header className="app-header">
          <a
            className="app-header__brand"
            href="#/"
            aria-label={t("auth.dashboardLabel")}
          >
            <BrandMark decorative />
          </a>
          <div className="app-header__utilities utility-bar__actions">
            <span
              className="role-label"
              title={
                state.session.role === "admin"
                  ? t("role.executiveAdmin")
                  : t("role.executiveViewer")
              }
            >
              <span className="role-label__avatar" aria-hidden="true">
                {state.session.role === "admin"
                  ? t("role.executiveAdminInitials")
                  : t("role.executiveViewerInitials")}
              </span>
              <span className="role-label__text">
                {state.session.role === "admin"
                  ? t("role.executiveAdmin")
                  : t("role.executiveViewer")}
              </span>
            </span>
            <LocaleToggle />
            <ThemeToggle theme={theme} onToggleTheme={onToggleTheme} />
            <button
              className="app-header__sign-out icon-button"
              type="button"
              aria-label={t("auth.signOut")}
              title={t("auth.signOut")}
              onClick={() => void controller.signOut()}
            >
              <SignOutIcon />
            </button>
          </div>
        </header>
        <main className="page-frame" id="main-content" tabIndex={-1}>
          {state.session.role !== "admin" &&
          ["revenue", "expenses", "users", "connect"].includes(route.page) ? (
            <section className="overview-state" role="alert">
              <h1>
                {t(
                  route.page === "users"
                    ? "adminUsers.error.permissionDenied"
                    : "page.adminAccessRequired",
                )}
              </h1>
              <a href="#/">{t("page.returnOverview")}</a>
            </section>
          ) : route.page === "overview" ? (
            <>
              <header className="page-heading">
                <div>
                  <h1>{t("page.overviewTitle")}</h1>
                  <p>{t("page.overviewSubtitle")}</p>
                </div>
                <span className="status-label">
                  <span aria-hidden="true" /> {t("session.authenticated")}
                </span>
              </header>
              <PortfolioOverviewPanel
                client={overviewClient}
                role={state.session.role}
              />
            </>
          ) : route.page === "conferences" ? (
            <>
              <header className="page-heading">
                <div>
                  <h1>{t("page.conferencesTitle")}</h1>
                  <p>{t("page.conferencesSubtitle")}</p>
                </div>
              </header>
              <ConferenceDirectoryPanel
                client={overviewClient}
                role={state.session.role}
              />
            </>
          ) : route.page === "conference" ? (
            <ConferenceDetailPanel
              client={overviewClient}
              conferenceId={route.conferenceId}
              role={state.session.role}
              initialTab={route.detailView}
            />
          ) : route.page === "revenue" ? (
            <>
              <header className="page-heading">
                <div>
                  <h1>{t("page.revenueTitle")}</h1>
                  <p>{t("page.revenueSubtitle")}</p>
                </div>
              </header>
              <RevenueDraftNavigationContext.Provider
                value={navigationDrafts.current}
              >
                <SponsorshipsPanel
                  client={overviewClient}
                  role={state.session.role}
                />
              </RevenueDraftNavigationContext.Provider>
            </>
          ) : route.page === "expenses" ? (
            <>
              <header className="page-heading">
                <div>
                  <h1>{t("page.expensesTitle")}</h1>
                  <p>{t("page.expensesSubtitle")}</p>
                </div>
              </header>
              <RevenueDraftNavigationContext.Provider
                value={navigationDrafts.current}
              >
                <ExpensesPanel
                  client={overviewClient}
                  role={state.session.role}
                  onDraftChange={(id, dirty) => {
                    if (dirty) navigationDrafts.current.add(id);
                    else navigationDrafts.current.delete(id);
                  }}
                />
              </RevenueDraftNavigationContext.Provider>
            </>
          ) : route.page === "connect" ? (
            <>
              <header className="page-heading">
                <div>
                  <h1>{t("page.connectTitle")}</h1>
                  <p>{t("page.connectSubtitle")}</p>
                </div>
              </header>
              <ConnectConferencePanel client={overviewClient} />
            </>
          ) : route.page === "users" ? (
            <>
              <header className="page-heading">
                <div>
                  <h1>{t("page.usersTitle")}</h1>
                  <p>{t("page.usersSubtitle")}</p>
                </div>
              </header>
              <UserAccessPanel api={adminUsersClient} />
              <AdminUsersPanel
                api={adminUsersClient}
                canManageUsers={state.session.role === "admin"}
              />
            </>
          ) : (
            <section className="overview-state">
              <h1>{t("page.plannedTitle")}</h1>
              <p>{t("page.plannedBody")}</p>
              <a
                className="button button--secondary overview-state__action"
                href="#/"
              >
                {t("page.returnOverview")}
              </a>
            </section>
          )}
        </main>

        <footer className="app-footer">
          <span className="app-footer__platform">
            {t("auth.dashboardLabel")}
          </span>
          <PartnerSignature placement="footer" />
        </footer>

        <nav
          className="mobile-navigation"
          aria-label={t("a11y.mobileNavigation")}
        >
          {mobilePrimaryNavigation.map(
            ({ labelKey, icon: Icon, href, page }) => (
              <a
                key={labelKey}
                href={href}
                aria-label={t(labelKey)}
                aria-current={
                  route.page === page ||
                  (page === "conferences" && route.page === "conference")
                    ? "page"
                    : undefined
                }
              >
                <Icon />
                <span>{t(labelKey)}</span>
              </a>
            ),
          )}
          <button
            ref={mobileMoreButtonRef}
            className={mobileMoreSelected ? "is-selected" : undefined}
            type="button"
            aria-expanded={mobileMoreOpen}
            aria-controls="mobile-more-menu"
            onClick={() => setMobileMoreOpen((open) => !open)}
          >
            <span className="more-icon" aria-hidden="true">
              •••
            </span>
            <span>{t("nav.more")}</span>
          </button>
        </nav>
        {mobileMoreOpen ? (
          <nav
            ref={mobileMoreMenuRef}
            className="mobile-more-menu"
            id="mobile-more-menu"
            aria-label={t("a11y.moreNavigation")}
          >
            {mobileMoreNavigation.map(
              ({ labelKey, icon: Icon, href, page }) => (
                <a
                  key={labelKey}
                  href={href}
                  aria-label={t(labelKey)}
                  aria-current={route.page === page ? "page" : undefined}
                >
                  <Icon />
                  <span>{t(labelKey)}</span>
                </a>
              ),
            )}
            <button type="button" onClick={() => void controller.signOut()}>
              {t("auth.signOut")}
            </button>
            <PartnerSignature placement="mobile" />
          </nav>
        ) : null}
      </div>
    </div>
  );
}

function InitializingScreen() {
  const { t } = useLocale();
  return (
    <main
      className="initializing-screen"
      aria-label={t("a11y.restoringSession")}
    >
      <BrandMark />
      <span className="initializing-screen__line" />
    </main>
  );
}

export function AuthApp({
  controller,
  overviewClient,
  adminUsersClient,
}: AuthAppProps) {
  const { t } = useLocale();
  const state = useSyncExternalStore(
    controller.subscribe,
    controller.getSnapshot,
    controller.getSnapshot,
  );
  const [theme, setTheme] = useState<AppTheme>(() => resolveInitialTheme());

  useEffect(() => {
    applyThemeToDocument(theme);
    persistTheme(theme);
  }, [theme]);

  function toggleTheme() {
    setTheme((current) => (current === "dark" ? "light" : "dark"));
  }

  useEffect(() => {
    void controller.restore();
  }, [controller]);

  if (state.view === "initializing") return <InitializingScreen />;
  if (state.view === "mfa-enrollment-success") {
    return (
      <AccountSuccess
        kind="mfa"
        onContinue={() => {
          window.history.replaceState(null, "", "/#/");
          controller.finishEnrollment();
        }}
      />
    );
  }
  if (state.view === "authenticated") {
    return (
      <AuthenticatedShell
        controller={controller}
        overviewClient={overviewClient}
        adminUsersClient={adminUsersClient}
        theme={theme}
        onToggleTheme={toggleTheme}
      />
    );
  }
  if (state.view === "mfa-challenge" || state.view === "mfa-enrollment") {
    return (
      <MfaPanel
        controller={controller}
        theme={theme}
        onToggleTheme={toggleTheme}
      />
    );
  }

  return (
    <main className="sign-in-page">
      <section
        className="institutional-field"
        aria-label={t("brand.universityIdentityAria")}
      >
        <div className="institutional-field__mark">
          <BrandMark inverse decorative layout="vertical" />
        </div>
        <PartnerSignature placement="hero" />
      </section>
      <SignInForm
        controller={controller}
        theme={theme}
        onToggleTheme={toggleTheme}
      />
    </main>
  );
}
